/* eslint-disable function-paren-newline */
import { useCallback, useMemo, useRef } from 'react';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { omit } from 'helpers/array';
import { useSort } from './useSort';
import { useTableTheme } from './useTableTheme';

export const useTable = ({
  items,
  columnDefinition,
  persistColumnKey,
  isLoadingMore,
  isInitialized,
  initialSortBy,
  onSort,
  canSelect,
  onSelect,
  selectedAll,
  onColumnAction,
  cellStyle = {},
  useHorizontalActions = true,
  canSort = true,
  sortColumns = [],
  useExternalStoredColumns = false,
  storedColumns = [],
  setStoredColumns = () => {},
}) => {
  const tableRef = useRef(null);

  const [localStoredSelectedColumns, localSetStoredSelectedColumns] = useLocalStorage(
    persistColumnKey || 'ag-grid-table-v1',
    columnDefinition.map((item) => ({
      ...item,
      show: typeof item.show === 'boolean' ? item.show : true,
    })),
  );

  const setStoredSelectedColumns = useCallback(
    (columns) => {
      if (useExternalStoredColumns) {
        setStoredColumns(columns);
      } else {
        localSetStoredSelectedColumns(columns);
      }
    },
    [useExternalStoredColumns, setStoredColumns, localSetStoredSelectedColumns],
  );

  const storedSelectedColumns = useMemo(() => {
    if (useExternalStoredColumns) {
      return storedColumns || localStoredSelectedColumns;
    }
    return localStoredSelectedColumns;
  }, [useExternalStoredColumns, storedColumns, localStoredSelectedColumns]);

  const selectedColumns = useMemo(
    () => storedSelectedColumns.filter((column) => columnDefinition.some((item) => item.accessor === column.accessor)),
    [storedSelectedColumns, columnDefinition],
  );

  const sortedColumns = useMemo(() => [...sortColumns, ...selectedColumns], [sortColumns, selectedColumns]);
  const theme = useTableTheme();
  const { selectedColumn, setSelectedColumn, handleSort } = useSort({
    columnDefinition: sortedColumns,
    initialSortBy,
    onSort,
    canSort,
  });

  const hideColumn = useCallback(
    (column) => {
      onColumnAction?.('hide', { payload: column });
      setStoredSelectedColumns(
        (prev) =>
          (prev || localStoredSelectedColumns).map((item) => {
            if (item.accessor === column.accessor) {
              return { ...item, show: false };
            }
            return item;
          }),
        // eslint-disable-next-line function-paren-newline
      );
      tableRef.current.api.refreshHeader();
    },
    [onColumnAction, setStoredSelectedColumns, localStoredSelectedColumns],
  );

  const flattenedColumns = useMemo(
    () =>
      columnDefinition.reduce((acc, col) => {
        acc[col.accessor] = col;
        return acc;
      }, {}),
    [columnDefinition],
  );

  const formatColumn = useCallback(
    (column) => {
      const {
        accessor,
        Header,
        disableSortBy,
        disablePadding,
        tooltip,
        cellRenderer,
        width,
        minWidth,
        flex,
        pinned,
        rowGroup,
        show,
        children,
        orderIndex,
        columnSelection,
        hideOnTableColumn,
        ...props
      } = flattenedColumns[column.accessor];

      const headerParams = {
        handleSort,
        field: accessor,
        sortedBy: initialSortBy,
        tooltip,
        hideColumn,
        disableSortBy,
        hideOnTableColumn,
        displayName: Header,
        rowGroup,
        columnSelection,
        itemCount: items.length,
      };

      const selected = selectedColumns.find((item) => item.accessor === accessor);
      const columnDef = {
        field: accessor,
        accessor,
        Header,
        headerName: Header,
        hide: selected?.show !== undefined ? !selected.show : true,
        sortable: false,
        cellRenderer: cellRenderer ?? 'tableRow',
        filter: false,
        pinned,
        rowGroup: rowGroup ?? false,
        headerComponent: 'tableHeader',
        headerComponentParams: headerParams,
        cellRendererParams: {
          isFirstColumn: selectedColumns[0].accessor === accessor,
          useHorizontalActions,
          cellStyle,
        },
        ...props,
      };

      // Only set width if flex is not present
      if (flex !== undefined) {
        columnDef.flex = flex;
        if (minWidth !== undefined) {
          columnDef.minWidth = minWidth;
        }
      } else {
        columnDef.width =
          typeof selected?.width === 'string'
            ? parseInt(selected?.width.replace('px', ''), 10)
            : selected?.width || width || 300;
        if (minWidth !== undefined) {
          columnDef.minWidth = minWidth;
        }
      }

      return columnDef;
    },
    [
      flattenedColumns,
      handleSort,
      initialSortBy,
      hideColumn,
      items.length,
      selectedColumns,
      useHorizontalActions,
      cellStyle,
    ],
  );

  const columnDefs = useMemo(() => {
    const columns = canSelect
      ? [
          {
            pinned: 'left',
            field: 'selected',
            accessor: 'selected',
            headerName: 'selected',
            minWidth: 32,
            resizable: false,
            width: 32,
            cellRenderer: 'customCheckboxRenderer',
            sortable: false,
            filter: false,
            lockPosition: 'left',
            suppressMovable: true,
            headerComponent: 'customCheckboxRenderer',
            headerClass: 'ag-header-cell-checkbox',
            headerComponentParams: {
              onSelect,
              selectedAll,
              isBulkSelection: true,
              sx: {
                pt: '8px',
              },
            },
            cellRendererParams: {
              onSelect,
              selectedAll: false,
              isBulkSelection: false,
            },
            contextMenuItems: ['copy', 'paste', 'delete'],
            cellClass: 'ag-column-checkbox',
          },
        ]
      : [];

    const defs = columns.concat(
      selectedColumns.reduce((acc, column) => {
        if (flattenedColumns[column.accessor]) {
          acc.push(formatColumn(column));
        }
        return acc;
      }, []),
    );

    return defs;
  }, [canSelect, flattenedColumns, formatColumn, onSelect, selectedAll, selectedColumns]);

  const onColumnMoved = useCallback(
    ({ column, toIndex, source, finished }) => {
      if (source === 'uiColumnMoved' && finished) {
        const index = selectedColumns.findIndex(({ accessor }) => accessor === column.colId);
        const newColumns = [...selectedColumns];
        const indexToMove = toIndex - 1;

        const isMovable = ![
          'selected',
          ...columnDefinition.filter((item) => item.suppressMovable).map((item) => item.accessor),
        ].includes(selectedColumns[indexToMove].accessor);

        if (indexToMove >= 0 && indexToMove < newColumns.length) {
          if (isMovable) {
            newColumns.splice(indexToMove, 0, newColumns.splice(index, 1)[0]);
          }

          setStoredSelectedColumns(newColumns);
        }
      }
    },
    [columnDefinition, selectedColumns, setStoredSelectedColumns],
  );

  const onColumnResized = useCallback(
    (params) => {
      setStoredSelectedColumns((prev) => {
        if (!prev) return prev;
        const width = params.column?.getActualWidth?.() || 300;
        return prev.map((item) => {
          if (item.accessor === params.column.getColId() && params.source === 'uiColumnResized') {
            return { ...item, width };
          }
          return item;
        });
      });
    },
    [setStoredSelectedColumns],
  );

  const showEmptyView = items.length === 0 && !isLoadingMore && isInitialized;

  const agColumnDefs = useMemo(
    () =>
      Object.values(columnDefs).map((item) => ({
        ...omit(item, ['Header', 'accessor', 'hideOnTableColumn']),
        headerComponentParams: {
          ...(item?.headerComponentParams ? item.headerComponentParams : {}),
          accessor: item.accessor,
        },
      })),
    [columnDefs],
  );

  return {
    tableRef,
    selectedColumns,
    setSelectedColumns: setStoredSelectedColumns,
    selectedColumn,
    setSelectedColumn,
    showEmptyView,
    handleSort,
    columnDefs: agColumnDefs,
    theme,
    flattenedColumns,
    onColumnMoved,
    sortedColumns,
    onColumnResized,
    hideColumn,
  };
};
